import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './styles.scss';
import { formatPrice } from '../../utility/utils';
import { useDispatch, useSelector } from 'react-redux';
import { HeartStraightIcon } from '@phosphor-icons/react';
import { removeWishListItem, setQuickAddItem, showPreview, updateCart, updateWishList } from '../../slices/app-slice';
import { motion } from "framer-motion";
import ItemAction from '../item-action';

// Constants
const ITEM_TYPES = {
    PRODUCTS: 'products',
    SLABS: 'slabs',
    BASES: 'bases',
    STANDARD: 'standard'
};

const TAG_TYPES = {
    BEST_SELLER: 'best seller',
    NEW_IN: 'new in',
    NONE: 'none'
};

const PILL_STYLES = {
    [TAG_TYPES.BEST_SELLER]: 'gold',
    [TAG_TYPES.NEW_IN]: 'coal',
    DEFAULT: 'bark',
    SALE: 'coral',
    QUICK_ADD: 'quick-add',
    QUICK_VIEW: 'quick-view',
    WISHLIST: 'wishlist'
};

const IMAGE_OPTIMIZATION = {
    WIDTH: 600,
    HEIGHT: 800,
    QUALITY: 90,
    FORMAT: 'webp'
};

const Item = ({ item, index, setItem, type = ITEM_TYPES.STANDARD }) => {
    const [image, setImage] = useState({});
    const [secondImage, setSecondImage] = useState(null);
    const [isWishlistItem, setIsWishListItem] = useState(false);

    const wishlist = useSelector((state) => state.app.wishList);
    const dispatch = useDispatch();

    // Helper functions
    const isProductType = () => [ITEM_TYPES.PRODUCTS, ITEM_TYPES.SLABS, ITEM_TYPES.BASES].includes(type);
    const hasValidTag = () => item?.tag && item?.tag !== TAG_TYPES.NONE && item?.tag.toLowerCase() !== 'none';
    const isInStock = () => item.stock && item.stock > 0;
    const hasSalePrice = () => !!item.salePrice;

    // Event handlers
    const handleQuickView = useCallback((event) => {
        event.stopPropagation();
        dispatch(setQuickAddItem({ item, type: "quickView" }));
        dispatch(showPreview(true));
    }, [dispatch, item]);

    const handleAddToWishList = useCallback((event) => {
        event.stopPropagation();

        if (isWishlistItem) {
            dispatch(removeWishListItem(item.id));
        } else {
            dispatch(updateWishList({ item, type: item.category }));
            dispatch(setQuickAddItem({ item }));
            dispatch(showPreview(true));
        }
    }, [dispatch, item, isWishlistItem]);

    const handleQuickAdd = useCallback((event) => {
        event.stopPropagation();
        dispatch(updateCart({ product: item, quantity: 1, total: item.price }));
        dispatch(setQuickAddItem({ product: item, quantity: 1, total: item.price }));
        dispatch(showPreview(true));
    }, [dispatch, item]);

    const handleImageHover = useCallback((hoverImage) => {
        if (hoverImage) {
            setImage(hoverImage);
        }
    }, []);

    const handleImageLeave = useCallback(() => {
        setImage(item.images[0]?.fileName);
    }, [item.images]);

    const handleItemClick = useCallback(() => {
        setItem(item);
    }, [setItem, item]);

    // Effects
    useEffect(() => {
        if (item?.images?.length) {
            setImage(item.images[0]?.fileName);
            setSecondImage(item.images.length > 1 ? item.images[1]?.fileName : null);
        }

        const isOnWishlist = wishlist.some(wishlistItem =>
            wishlistItem.item.id === item.id && wishlistItem.item.title === item.title
        );
        setIsWishListItem(isOnWishlist);
    }, [item, wishlist]);

    // Helper functions for rendering
    const getPillStyle = (tag) => {
        if (!tag) return PILL_STYLES.DEFAULT;
        const lowerTag = tag.toLowerCase();
        return PILL_STYLES[lowerTag] || PILL_STYLES.DEFAULT;
    };

    const getImageUrl = (imagePath, imageType = type) => {
        const baseUrl = process.env.REACT_APP_WEBSITE_API;
        const path = imageType === ITEM_TYPES.STANDARD ? 'projects' : imageType;
        return `${baseUrl}/api/images/optimize?filePath=images/${path}/${imagePath}&width=${IMAGE_OPTIMIZATION.WIDTH}&height=${IMAGE_OPTIMIZATION.HEIGHT}&quality=${IMAGE_OPTIMIZATION.QUALITY}&format=${IMAGE_OPTIMIZATION.FORMAT}`;
    };

    const shouldShowTag = () => hasValidTag() || type === ITEM_TYPES.SLABS || hasSalePrice() || !isInStock();

    // Render helper components
    const TagPills = () => {
        const isSlabOrBase = type === ITEM_TYPES.SLABS || type === ITEM_TYPES.BASES;
        
        // Collect all non-wishlist pills for slabs and bases
        const leftPills = [];
        
        if (!hasSalePrice() && hasValidTag()) {
            leftPills.push(
                <div key="tag" className={`pill ${getPillStyle(item.tag)}`}>
                    {item.tag?.toLowerCase() === TAG_TYPES.NEW_IN.toLowerCase() ? 'New' : item.tag}
                </div>
            );
        }

        if (hasSalePrice()) {
            leftPills.push(
                <div key="sale" className={`pill ${PILL_STYLES.SALE}`}>Sale</div>
            );
        }

        if (isInStock()) {
            if (type === ITEM_TYPES.PRODUCTS) {
                leftPills.push(
                    <ItemAction onActionClick={handleQuickAdd} />
                );
            }
        } else {
            // if (type === ITEM_TYPES.PRODUCTS) {
            //     leftPills.push(
            //         <div key="order" className={`pill ${PILL_STYLES.DEFAULT}`}>Order this</div>
            //     );
            // }
        }

        if (type === ITEM_TYPES.SLABS && (item.startingPrice || item.seats || item.origin || item.grain)) {
            leftPills.push(
                <ItemAction onActionClick={handleQuickView} />
            );
        }

        return (
            <div className={`item-tag ${shouldShowTag() ? "has-tag" : ""}`}>
                {isSlabOrBase ? (
                    <>
                        <div className="pills-left">
                            {leftPills}
                        </div>
                        <div
                            title="Wishlist this item"
                            className={`pill ${PILL_STYLES.WISHLIST}`}
                            onClick={handleQuickView}
                        >
                            <HeartStraightIcon
                                size={22}
                                className={`wishlist-icon ${isWishlistItem ? "full" : ""}`}
                                onClick={handleAddToWishList}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        {leftPills}
                        {(type === ITEM_TYPES.SLABS || type === ITEM_TYPES.BASES) && (
                            <div
                                title="Wishlist this item"
                                className={`pill ${PILL_STYLES.WISHLIST}`}
                                onClick={handleQuickView}
                            >
                                <HeartStraightIcon
                                    size={22}
                                    className={`wishlist-icon ${isWishlistItem ? "full" : ""}`}
                                    onClick={handleAddToWishList}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const ProductImage = () => (
        <motion.div
            key={image}
            variants={variants}
            animate="show"
            initial="hide"
            className="item-img-container"
        >
            <div className="item-img">
                <div className="image-loader">
                    <div className="loader"></div>
                </div>
                <img alt="" src={getImageUrl(image)} />

            </div>
        </motion.div>
    );

    const ProductDetails = () => (
        <div className={`item-details ${item.lightText ? "light" : ""}`}>
            <p>{item?.title}</p>
            <div className="price-wrapper">
                {type === ITEM_TYPES.PRODUCTS && (
                    <>
                        <p className={hasSalePrice() ? "strike-through" : ""}>
                            {formatPrice(item?.price)}
                        </p>
                        {hasSalePrice() && (
                            <p className="sale-price">{formatPrice(item?.salePrice)}</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    const VariantPickers = () => (
        <div className="variant-pickers">
            {Array.isArray(item?.variants) &&
                item.variants
                    .filter(variant => variant.type === "Material")
                    .map((variant, i) => (
                        <div
                            key={i}
                            onClick={() => setItem(variant)}
                            title={variant.title}
                            className="variant-picker"
                        >
                            {variant.thumbnail && (
                                <img
                                    src={`${process.env.REACT_APP_WEBSITE_API}/images/thumbnails/${variant.thumbnail}`}
                                    alt=""
                                />
                            )}
                        </div>
                    ))
            }
        </div>
    );

    const StandardItem = () => (
        <div className="item hover-zoom" onClick={handleItemClick}>
            {image && (
                <div
                    className="item-bg"
                    style={{
                        backgroundImage: `url("${getImageUrl(image, 'projects')}")`
                    }}
                />
            )}
            <div className="item-overlay">
                {item.title && <p>{item.title}</p>}
                {item.type && <p>{item.type}</p>}
            </div>
        </div>
    );

    const ProductItem = () => (
        item && image && (
            <div className="item-product-wrapper">
                <div
                    onMouseLeave={handleImageLeave}
                    onMouseEnter={() => handleImageHover(secondImage || image)}
                    onClick={handleItemClick}
                    className="item-product"
                >
                    <TagPills />
                    <ProductImage />
                    <ProductDetails />


                    {item.visibility === "Sale Pending" && (
                        <div className="sale-pending">
                            Sale Pending
                        </div>
                    )}
                </div>
                <VariantPickers />
            </div>
        )
    );

    return isProductType() ? <ProductItem /> : <StandardItem />;
}

// PropTypes for better type validation
Item.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title: PropTypes.string,
        price: PropTypes.number,
        salePrice: PropTypes.number,
        tag: PropTypes.string,
        stock: PropTypes.number,
        images: PropTypes.arrayOf(PropTypes.shape({
            fileName: PropTypes.string
        })),
        variants: PropTypes.arrayOf(PropTypes.shape({
            type: PropTypes.string,
            title: PropTypes.string,
            thumbnail: PropTypes.string
        })),
        lightText: PropTypes.bool,
        startingPrice: PropTypes.number,
        seats: PropTypes.number,
        origin: PropTypes.string,
        grain: PropTypes.string,
        category: PropTypes.string
    }).isRequired,
    index: PropTypes.number,
    setItem: PropTypes.func.isRequired,
    type: PropTypes.oneOf(Object.values(ITEM_TYPES))
};

Item.defaultProps = {
    type: ITEM_TYPES.STANDARD
};

export default Item;

export const variants = {
    show: {
        opacity: 1,
        y: 0,
        transition: {
            ease: 'easeIn',
            duration: 0.5
        }
    },
    hide: {
        y: 0,
        opacity: 0,
        transition: {
            ease: 'easeOut',
            duration: 0.5
        }
    }
};